<template>
  <div>
    <p>
      <span class="font-medium">{{ t('rentStart') }}:</span>
      {{ formattedStartDate }} | {{ pickupLocationInfo }}
    </p>
    <p>
      <span class="font-medium">{{ t('rentEnd') }}:</span>
      {{ formattedEndDate }} | {{ dropoffLocationInfo }}
    </p>
    <p>
      <span class="font-medium">{{ t('rentDuration') }}:</span>
      {{ pricing?.days }} {{ t('days') }}
    </p>
    <div v-if="longTermTotals">
      <p class="font-medium">
        {{ t('firstPeriodPrice') }}:
        {{ formatCurrency(longTermTotals.firstPeriodPrice) }}
      </p>
      <p class="font-medium">
        {{ t('totalPrice') }}:
        {{ formatCurrency(longTermTotals.contractTotal) }}
      </p>
    </div>
    <div v-else-if="totalBasePrice">
      <p class="font-medium">
        {{ t('price') }}:
        {{ formatCurrency(totalBasePrice) }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getDateTimeFormat } from '@/hooks/use-date-format';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useLocationInfo } from '@/hooks/use-location-info';
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/utils/format-numbers';
import type { CreateBookingProps } from '@/views/bookings/booking-create/components/use-create-booking-props';
import { computed } from 'vue';
import {
  getContractTotal,
  getPeriodPrice,
  getTotalBasePrice,
  isLongTermPricing,
} from '@/utils/pricing.utils';

const props = defineProps<{
  createBookingProps: CreateBookingProps;
}>();

const { t } = useI18n();

const { startDate, endDate, pickupLocation, dropoffLocation, pricing } = props.createBookingProps;
const totalBasePrice = computed(() =>
  pricing.value ? getTotalBasePrice(pricing.value) : undefined,
);
const longTermTotals = computed(() => {
  const quote = pricing.value;
  if (!quote || !isLongTermPricing(quote) || !quote.periods?.[0]) {
    return null;
  }
  return {
    firstPeriodPrice: getPeriodPrice(quote.periods[0]),
    contractTotal: getContractTotal(quote),
  };
});

const formattedStartDate = useFormattedDate(startDate, getDateTimeFormat());
const formattedEndDate = useFormattedDate(endDate, getDateTimeFormat());
const pickupLocationInfo = useLocationInfo(pickupLocation);
const dropoffLocationInfo = useLocationInfo(dropoffLocation);
</script>

<i18n lang="json">
{
  "en": {
    "rentStart": "Rent Start",
    "rentEnd": "Rent End",
    "rentDuration": "Rent Duration",
    "days": "days",
    "price": "Price",
    "firstPeriodPrice": "First period",
    "totalPrice": "Total price"
  },
  "de": {
    "rentStart": "Mietbeginn",
    "rentEnd": "Mietende",
    "rentDuration": "Mietdauer",
    "days": "Tage",
    "price": "Preis",
    "firstPeriodPrice": "1. Zeitraum",
    "totalPrice": "Gesamtpreis"
  },
  "es": {
    "rentStart": "Inicio del alquiler",
    "rentEnd": "Fin del alquiler",
    "rentDuration": "Duración del alquiler",
    "days": "días",
    "price": "Precio",
    "firstPeriodPrice": "Primer periodo",
    "totalPrice": "Precio total"
  }
}
</i18n>
